import { useCallback, useEffect, useMemo } from "react";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Input, Form, Button, Typography } from "antd";

import {
  editVehicle, getAllDropdownUsers,
  getClients,
  getDropdownUsers, getDropdownUsersByClientID, getPackageList, getPlanList, getSubscriptionGroupsByClient, initializeZones,
  setModalName,
  setModels,
} from "store/actions";
import { parseClients, parseModels, parseUsers, parseZoneOptions } from "utils/parsers";
import { VEHICLE_FORMIK } from "utils/formik-data";
import { FormItem, SelectSearch } from "components";
import { AppState } from "store";
import { VehicleValues } from "types/formik";
import { getFullName } from "utils/helpers";
import { VehicleServiceStatus } from "../protobuf/vehicle_service_pb";
import { VEHICLE_SERVICE_STATUS, vehicleColors } from "../utils/mapped-data";
import { getPackageCode, getPlanCode, parsePackages, parsePlans } from "../utils/parsers/billing-parser";

const { Title, Text } = Typography;

export default function EditVehiclesForm() {
  const dispatch = useDispatch();
  const {
    vehicles: { models, clients, selectedVehicle },
    users: { dropdownUsers },
    zones: { zones },
    common: { userType = 1 },
    billing: { packages, plans, subscriptionGroups }
  } = useSelector<AppState, AppState>((state) => state);

  const modelsOptions = useMemo(() => parseModels(models), [models]);
  const usersOptions = useMemo(() => parseUsers(dropdownUsers), [dropdownUsers]);
  const clientOptions = useMemo(() => parseClients(clients), [clients]);
  const zonesOptions = useMemo(() => parseZoneOptions(zones), [zones]);
  const packagesList = useMemo(() => parsePackages(packages), [packages])
  const planList = useMemo(() => parsePlans(plans), [plans])
  const subscriptionGroupOptions = useMemo(
    () => (subscriptionGroups || []).map((g) => ({ label: g.name, value: g.name })),
    [subscriptionGroups]
  );

  const onCancel = useCallback(() => {
    dispatch(setModalName(undefined));
  }, [dispatch]);

  const onSubmit = useCallback(
    (values: VehicleValues) => {
      dispatch(
        editVehicle({
          id: selectedVehicle?.id || "",
          licensePlate: values.vehicle_id.trim(),
          modelId: values.vehicle_model,
          ownerOrgId: values?.client || "",
          currentlyAssignedToArrayList: values.assign_to,
          locationName: values.location?.trim(),
          deviceIotId: values.iot_id,
          zoneId: values.zone,
          vehicleServiceStatus: values.vehicle_service_status,
          networkId: values.network_id,
          color: values.color,
          package_id: values.package_id || "",
          plan_id: values.plan_id || "",
          package_code: getPackageCode(packages, values.package_id || ""),
          plan_code: getPlanCode(plans, values.plan_id || ""),
          subscription_group_id: values.subscription_group_id || "",
        })
      );
      onCancel();
    },
    [dispatch, onCancel, selectedVehicle?.id, packages, plans]
  );

  const onClientChange = function (value: string) {
    console.log("id : ", value)
    dispatch(getDropdownUsersByClientID(value))
    if (value) {
      dispatch(getSubscriptionGroupsByClient({ clientId: value }))
    }
  }

  const vehicleServiceOption = Object.keys(VehicleServiceStatus.Enum).map((key, index) => ({
    label: VEHICLE_SERVICE_STATUS[index as VehicleServiceStatus.Enum],
    value: index,
  })).filter((opt) => {
    return opt.label != "Unknown"
  })

  const initialValues: VehicleValues = useMemo(() => {
    return {
      vehicle_id: selectedVehicle?.licensePlate || "",
      vehicle_model: selectedVehicle?.modelId || "",
      client: selectedVehicle?.clientOrgId || "",
      assign_to: selectedVehicle?.assignee?.map((u) => u.id) || [],
      zone: selectedVehicle?.zoneId || "",
      location: selectedVehicle?.locationName || "",
      iot_id: selectedVehicle?.deviceIotId || "",
      vin: selectedVehicle?.vin?.trim() || "",
      vehicle_service_status: selectedVehicle?.vehicleServiceStatus !== undefined
        ? selectedVehicle?.vehicleServiceStatus
        : 0,
      network_id: selectedVehicle?.networkId || "",
      color: selectedVehicle?.color || 1,
      package_id: selectedVehicle?.packageId || "",
      plan_id: selectedVehicle?.planId || "",
      package_code: "",
      plan_code: "",
      subscription_group_id: selectedVehicle?.subscriptionGroup || "",
    };
  }, [selectedVehicle]);

  const onPackageChange = function (value: string) {
    console.log("id : ", value)
    dispatch(getPlanList({ id: value || "" }))
  }

  useEffect(() => {
    dispatch(setModels());
    dispatch(getClients());
    dispatch(initializeZones())
    dispatch(getPackageList())
    if (selectedVehicle?.packageId != "") {
      dispatch(getPlanList({ id: selectedVehicle?.packageId || "" }))
    }
    if (selectedVehicle?.clientOrgId) {
      dispatch(getSubscriptionGroupsByClient({ clientId: selectedVehicle.clientOrgId }))
    }
    if (userType == 1 || userType == 2) {
      console.log("selectedVehicle?.clientOrgId : ", selectedVehicle?.clientOrgId)
      if (selectedVehicle?.clientOrgId != "") {
        dispatch(getDropdownUsersByClientID(selectedVehicle?.clientOrgId))
      } else {
        dispatch(getAllDropdownUsers())
      }
    } else {
      dispatch(getDropdownUsers())
    }
  }, [dispatch, userType, selectedVehicle?.clientOrgId]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={VEHICLE_FORMIK.validationSchema}
      onSubmit={onSubmit}
      enableReinitialize>
      {({
        values,
        errors,
        isValid,
        dirty,
        handleSubmit,
        handleChange,
        setFieldValue,
      }) => {
        console.log("Form state:", { isValid, dirty, errors, values });

        return (
          <div className="form-container">
            <Title level={5}>Edit Vehicle</Title>
            <Text className="description" type="secondary">
              Modify the information below and hit save to update
            </Text>
            <Form onFinish={handleSubmit} className="--add-vehicles-form">
              <div className="form-fields-section">
                <div className="form-field-wrapper">
                  <FormItem label="Vehicle ID" required error={errors.vehicle_id}>
                    <Input
                      name="vehicle_id"
                      value={values.vehicle_id}
                      onChange={handleChange}
                      placeholder="Vehicle ID"
                      disabled={userType == 1 || userType == 2 ? false : true}
                    />
                  </FormItem>
                </div>

                <div className="form-field-wrapper">
                  <FormItem
                    label="Model"
                    error={errors.vehicle_model}
                    required>
                    <SelectSearch
                      value={values.vehicle_model}
                      items={modelsOptions}
                      setFieldValue={setFieldValue}
                      fieldName="vehicle_model"
                      placeholder="Select"
                      disabled={true}
                    />
                  </FormItem>
                </div>

                <div className="form-field-wrapper">
                  {userType <= 2 && (
                    <FormItem label="Device ID" error={errors.iot_id}>
                      <Input
                        name="iot_id"
                        value={values.iot_id}
                        onChange={handleChange}
                        placeholder="000000000000000"
                      />
                    </FormItem>
                  )}
                </div>

                <div className="form-field-wrapper">
                  <FormItem error={errors.zone} label="Zone">
                    <SelectSearch
                      value={values?.zone || ""}
                      items={zonesOptions}
                      setFieldValue={setFieldValue}
                      fieldName="zone"
                      placeholder="Select "
                    />
                  </FormItem>
                </div>

                <div className="form-field-wrapper">
                  {(userType <= 2 || userType == 7) && (
                    <FormItem error={errors.client} label="Client">
                      <SelectSearch
                        value={values?.client || ""}
                        items={clientOptions}
                        setFieldValue={setFieldValue}
                        fieldName="client"
                        placeholder="Select "
                        onChangeProp={onClientChange}
                      />
                    </FormItem>
                  )}
                </div>

                {(userType <= 2 || userType == 7) && values.client && (
                  <div className="form-field-wrapper">
                    <FormItem label="Subscription Group" error={errors.subscription_group_id as string}>
                      <SelectSearch
                        value={values.subscription_group_id || ""}
                        items={subscriptionGroupOptions}
                        setFieldValue={setFieldValue}
                        fieldName="subscription_group_id"
                        placeholder="Select subscription group"
                      />
                    </FormItem>
                  </div>
                )}

                <div className="form-field-wrapper">
                  {userType != 8 && (
                    <FormItem error={errors.assign_to as string} label="Assignee">
                      <SelectSearch
                        value={values.assign_to}
                        items={usersOptions}
                        setFieldValue={setFieldValue}
                        fieldName="assign_to"
                        placeholder="Select User"
                        isMultiple
                      />
                    </FormItem>
                  )}
                </div>

                {(userType <= 2 || userType == 8) && (
                  <>
                    <div className="form-field-wrapper">
                      <FormItem error={errors.vehicle_service_status as string} label="Service Status">
                        <SelectSearch
                          value={values.vehicle_service_status || 1}
                          items={vehicleServiceOption}
                          setFieldValue={setFieldValue}
                          fieldName="vehicle_service_status"
                          placeholder="Select"
                        />
                      </FormItem>
                    </div>

                    <div className="form-field-wrapper">
                      <FormItem error={errors.color} label="Color">
                        <SelectSearch
                          value={values.color || 1}
                          items={vehicleColors}
                          setFieldValue={setFieldValue}
                          fieldName="color"
                          placeholder="Select "
                        />
                      </FormItem>
                    </div>
                  </>
                )}

                {(userType <= 2 || userType == 8) && (
                  <div className="form-field-wrapper">
                    <FormItem label="Network ID" error={errors.network_id}>
                      <Input
                        name="network_id"
                        value={values.network_id}
                        onChange={handleChange}
                        placeholder="03001122123"
                      />
                    </FormItem>
                  </div>
                )}
              </div>
              {(userType <= 2 || userType == 7) && <>
                <div className="form-field-wrapper">
                  <FormItem error={errors.package_id} label="Pricing Template">
                    <SelectSearch
                      value={values.package_id || ""}
                      items={packagesList}
                      setFieldValue={setFieldValue}
                      fieldName="package_id"
                      placeholder="Select "
                      onChangeProp={onPackageChange}
                    />
                  </FormItem>
                </div>
                <div className="form-field-wrapper">
                  <FormItem error={errors.plan_id} label="Plan">
                    <SelectSearch
                      value={values.plan_id || ""}
                      items={planList}
                      setFieldValue={setFieldValue}
                      fieldName="plan_id"
                      placeholder="Select "
                    />
                  </FormItem>
                </div>
              </>}
              <div className="action-btn-container">
                <Button className="cancel-btn" onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={!isValid || !dirty}>
                  Save
                </Button>
              </div>
            </Form>
          </div>
        );
      }}
    </Formik>
  );
}